import { writesApi } from './api';

// ── Dispute domain types ───────────────────────────────────────────────────
export type DisputeType =
  | 'product_not_received'
  | 'damaged_product'
  | 'wrong_product'
  | 'missing_items'
  | 'poor_quality'
  | 'other';

export type DisputeStatus =
  | 'open'
  | 'under_review'
  | 'awaiting_merchant_response'
  | 'resolved'
  | 'closed';

export type DisputePriority = 'low' | 'medium' | 'high' | 'urgent';

export type DisputeResolutionRequested =
  | 'full_refund'
  | 'partial_refund'
  | 'replacement'
  | 'store_credit';

export type DisputeSenderType = 'customer' | 'merchant' | 'internal_staff';

export interface Dispute {
  id: string;
  orderId: string;
  basketId: string | null;
  customerId: string;
  businessId: string;
  disputeType: DisputeType;
  subject: string;
  description: string;
  resolutionRequested: DisputeResolutionRequested;
  disputedAmount: number;
  evidenceUrls: string[] | null;
  status: DisputeStatus;
  priority: DisputePriority;
  raisedAt: string;
  deadline: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  resolutionNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DisputeMessage {
  id: string;
  disputeId: string;
  senderType: DisputeSenderType;
  senderId: string;
  senderName: string;
  message: string;
  attachments: string[] | null;
  isInternalNote: boolean;
  createdAt: string;
}

// Allowed status transitions, mirroring the backend rules
// (409 INVALID_TRANSITION for anything else).
export const ALLOWED_TRANSITIONS: Record<DisputeStatus, DisputeStatus[]> = {
  open: ['under_review', 'resolved', 'closed'],
  under_review: ['awaiting_merchant_response', 'resolved', 'closed'],
  awaiting_merchant_response: ['under_review', 'resolved', 'closed'],
  resolved: ['closed'],
  closed: [],
};

// ── Service: staff dispute management → Spring Boot :8080 ──────────────────
export const disputesService = {
  list: (params?: { status?: DisputeStatus; priority?: DisputePriority }) =>
    writesApi.get<Dispute[]>('/admin/disputes', params as Record<string, unknown>),

  get: (disputeId: string) =>
    writesApi.get<Dispute>(`/admin/disputes/${disputeId}`),

  // Includes internal notes (isInternalNote = true)
  getMessages: (disputeId: string) =>
    writesApi.get<DisputeMessage[]>(`/admin/disputes/${disputeId}/messages`),

  postMessage: (
    disputeId: string,
    payload: { message: string; attachments?: string[]; isInternalNote?: boolean },
  ) => writesApi.post<DisputeMessage>(`/admin/disputes/${disputeId}/messages`, payload),

  // Setting awaiting_merchant_response starts a 48h deadline + notifies the merchant.
  update: (disputeId: string, payload: { status?: DisputeStatus; priority?: DisputePriority }) =>
    writesApi.patch<Dispute>(`/admin/disputes/${disputeId}`, payload),

  // Marks resolved + notifies both parties. Refund money movement is NOT
  // automatic — run refunds through the admin payment-reversal flow.
  resolve: (disputeId: string, payload?: { resolutionNotes?: string }) =>
    writesApi.post<Dispute>(`/admin/disputes/${disputeId}/resolve`, payload ?? {}),
};
